import { computed, nextTick, ref } from 'vue'
import { useStore } from 'vuex'
import DonationAmountPicker from 'components/DonationAmountPicker/DonationAmountPicker.vue'
import FormInputTextField from 'components/FormInputTextField/FormInputTextField.vue'
import FormInputTextarea from 'components/FormInputTextarea/FormInputTextarea.vue'
import TooltipComponent from 'components/TooltipComponent/TooltipComponent.vue'
import nameValidator from 'foundation/js/inputValidators/nameValidator'
import phoneValidator from 'foundation/js/inputValidators/phoneValidator'
import cprValidator from 'foundation/js/inputValidators/cprValidator'

const PAYMENT_METHOD_MOBILEPAY_OVERLAY = 5
const CUSTOMER_TYPE_PRIVATE = 'Private'

export default {
    name: 'PersonalFundraisingMobilePayOverlay',
    components: {
        DonationAmountPicker,
        FormInputTextField,
        FormInputTextarea,
        TooltipComponent
    },
    setup() {
        const store = useStore()
        const firstName = ref('')
        const lastName = ref('')
        const personalGreeting = ref('')
        const phone = ref('')
        const cprNumber = ref('')
        const taxDeduction = ref(false)
        const anonymousDonation = ref('0')
        const donationAmount = ref(null)
        const submitError = ref('')
        const unSubmitted = ref(true)

        const getLabel = (labelKey) =>
            store.getters['labels/getLabel']({
                labelCollection: 'PersonalFundraisingMobilePayOverlay',
                labelKey
            })

        const overlayData = computed(() => store.state.main.page_overlay_content_data ?? {})

        const fieldVisibility = computed(() => {
            const hide = {
                hideIntroText: false,
                hideDonationAmountSelector: false,
                hideAnonymousOrPersonalDonation: false,
                hidePersonalGreeting: false,
                hideTaxDeduction: false,
                ...(overlayData.value.fieldVisibility ?? {})
            }

            return {
                showIntroText: !hide.hideIntroText,
                showDonationAmountSelector: !hide.hideDonationAmountSelector,
                showAnonymousOrPersonalDonation: !hide.hideAnonymousOrPersonalDonation,
                showPersonalGreeting: !hide.hidePersonalGreeting,
                showTaxDeduction: !hide.hideTaxDeduction
            }
        })

        const donationAmounts = computed(() => {
            const fromStore = overlayData.value.donationAmounts
            if (!Array.isArray(fromStore)) {
                return []
            }

            return fromStore
                .map((value) => Number.parseInt(value, 10))
                .filter((value) => Number.isFinite(value) && value > 0)
        })

        const hasDonationAmounts = computed(() => donationAmounts.value.length > 0)
        const showDonationAmountSelector = computed(
            () => fieldVisibility.value.showDonationAmountSelector && hasDonationAmounts.value
        )
        const showCustomAmountOnly = computed(
            () => !fieldVisibility.value.showDonationAmountSelector
        )
        const showDonationAmountSection = computed(
            () =>
                showCustomAmountOnly.value ||
                fieldVisibility.value.showDonationAmountSelector
        )

        const initialDonationAmount = computed(() => {
            if (showCustomAmountOnly.value) {
                return null
            }

            const fromStore = Number.parseInt(overlayData.value.defaultDonationAmount, 10)
            if (Number.isFinite(fromStore) && donationAmounts.value.includes(fromStore)) {
                return fromStore
            }

            return null
        })

        const fundraisingCampaignId = computed(() => overlayData.value.fundraisingCampaignId)
        const createDonationPageId = computed(() => overlayData.value.createDonationPageId)
        const isSubmitting = computed(() => store.state.fundraising.isSubmitting)

        const isAnonymous = computed(() => anonymousDonation.value === '1')
        const showCpr = computed(
            () => fieldVisibility.value.showTaxDeduction && taxDeduction.value
        )
        const showPersonalGreetingField = computed(
            () =>
                fieldVisibility.value.showPersonalGreeting &&
                !isAnonymous.value
        )

        const isAmountValid = computed(() => Number(donationAmount.value) > 0)
        const isFirstNameValid = computed(() => nameValidator(firstName.value))
        const isLastNameValid = computed(() => nameValidator(lastName.value))
        const isPhoneValid = computed(() => phoneValidator(phone.value))
        const isCprValid = computed(() => !showCpr.value || cprValidator(cprNumber.value))
        const hasDonationContext = computed(
            () => !!fundraisingCampaignId.value && Number(createDonationPageId.value) > 0
        )

        const canSubmit = computed(
            () =>
                isAmountValid.value &&
                isFirstNameValid.value &&
                isLastNameValid.value &&
                isPhoneValid.value &&
                isCprValid.value &&
                hasDonationContext.value &&
                !isSubmitting.value
        )

        const showAmountError = computed(() => !unSubmitted.value && !isAmountValid.value)
        const showFirstNameError = computed(() => !unSubmitted.value && !isFirstNameValid.value)
        const showLastNameError = computed(() => !unSubmitted.value && !isLastNameValid.value)
        const showPhoneError = computed(() => !unSubmitted.value && !isPhoneValid.value)
        const showCprError = computed(() => !unSubmitted.value && !isCprValid.value)

        const setDonationAmount = (amount) => {
            donationAmount.value = amount
        }

        const scrollToFirstInvalidField = async () => {
            await nextTick()
            const root = document.querySelector('.kb-personal-fundraising-mobilepay-overlay')
            const errorElement = root?.querySelector('.Form__Element__ValidationError, .validation-error')
            if (!errorElement) {
                return
            }

            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
            const field =
                errorElement.closest('.Form__Element')?.querySelector('input, textarea, [contenteditable="true"]') ??
                root?.querySelector('.kb-donation-amount-picker input:not([type="hidden"])')
            if (field && typeof field.focus === 'function') {
                field.focus({ preventScroll: true })
            }
        }

        const goToMobilePay = async () => {
            if (isSubmitting.value) {
                return
            }

            unSubmitted.value = false
            if (!canSubmit.value) {
                await scrollToFirstInvalidField()
                return
            }

            submitError.value = ''
            const isTaxDeductible =
                fieldVisibility.value.showTaxDeduction && taxDeduction.value

            const submitted = await store.dispatch('fundraising/submitPersonalMobilePayDonation', {
                fundraisingCampaignId: fundraisingCampaignId.value,
                payload: {
                    pageId: createDonationPageId.value,
                    paymentMethod: PAYMENT_METHOD_MOBILEPAY_OVERLAY,
                    useTestCreditCard: !!overlayData.value.useTestCreditCard,
                    totalPrice: Number(donationAmount.value),
                    addressLine1: null,
                    postalCode: null,
                    city: null,
                    mobilePhone: phone.value,
                    contactFirstName: firstName.value,
                    contactLastName: lastName.value,
                    cprNo: isTaxDeductible ? cprNumber.value : null,
                    cvrNo: null,
                    companyName: null,
                    comments: showPersonalGreetingField.value
                        ? personalGreeting.value.trim() || null
                        : null,
                    isTaxDeductible,
                    isAnonymous: fieldVisibility.value.showAnonymousOrPersonalDonation
                        ? isAnonymous.value
                        : true,
                    customerType: CUSTOMER_TYPE_PRIVATE,
                    referrer: document.referrer
                }
            })

            if (!submitted) {
                submitError.value = getLabel('submitErrorLabel')
            }
        }

        return {
            firstName,
            lastName,
            personalGreeting,
            phone,
            cprNumber,
            taxDeduction,
            anonymousDonation,
            donationAmount,
            donationAmounts,
            showDonationAmountSelector,
            showCustomAmountOnly,
            showDonationAmountSection,
            initialDonationAmount,
            fieldVisibility,
            isAnonymous,
            showCpr,
            showPersonalGreetingField,
            showAmountError,
            showFirstNameError,
            showLastNameError,
            showPhoneError,
            showCprError,
            isSubmitting,
            submitError,
            getLabel,
            setDonationAmount,
            goToMobilePay
        }
    }
}
